import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { api } from "./api";

let _messaging = null;
let _cfg = null;

// Load runtime FCM config from the backend and init the Firebase app (idempotent).
export async function initPush() {
    try {
        if (!(await isSupported())) return { configured: false, supported: false };
    } catch {
        return { configured: false, supported: false };
    }
    if (_messaging) return { configured: true, supported: true };
    const { data } = await api.get("/push/config");
    if (!data.configured) return { configured: false, supported: true };
    _cfg = data;
    const app = getApps()[0] || initializeApp(data.config);
    _messaging = getMessaging(app);
    return { configured: true, supported: true };
}

// Request permission, register the SW, fetch an FCM token and store it server-side.
// Must be called from a user gesture (button click).
export async function enablePush(onForeground) {
    // iOS only supports web push for apps installed to the Home Screen (iOS 16.4+).
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isStandalone = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
    if (isIOS && !isStandalone) return { ok: false, reason: "ios-needs-install" };

    const info = await initPush();
    if (!info.supported) return { ok: false, reason: "unsupported" };
    if (!info.configured) return { ok: false, reason: "not-configured" };

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: permission };

    try {
        const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams(_cfg.config).toString()}`;
        const registration = await navigator.serviceWorker.register(swUrl);
        const token = await getToken(_messaging, {
            vapidKey: _cfg.vapid_key,
            serviceWorkerRegistration: registration,
        });
        if (!token) return { ok: false, reason: "no-token" };
        await api.post("/push/register", { token, platform: "web" });
        if (onForeground) onMessage(_messaging, onForeground);
        return { ok: true, token };
    } catch (e) {
        const code = e?.code || "";
        if (code === "messaging/permission-blocked") return { ok: false, reason: "denied" };
        return { ok: false, reason: "error", error: String(e?.message || e) };
    }
}

// Wire the foreground listener without prompting (for already-granted sessions).
export async function listenForeground(onForeground) {
    const info = await initPush();
    if (info.configured && _messaging && onForeground) onMessage(_messaging, onForeground);
    return info;
}

// If permission is already granted, silently (re)register the device token and wire
// the foreground handler — so the phone keeps ringing without the user re-enabling.
export async function refreshPushToken(onForeground) {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
        return { ok: false, reason: "not-granted" };
    }
    const info = await initPush();
    if (!info.configured || !_messaging) return { ok: false, reason: "not-configured" };
    try {
        const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams(_cfg.config).toString()}`;
        const registration = await navigator.serviceWorker.register(swUrl);
        const token = await getToken(_messaging, {
            vapidKey: _cfg.vapid_key,
            serviceWorkerRegistration: registration,
        });
        if (!token) return { ok: false, reason: "no-token" };
        await api.post("/push/register", { token, platform: "web" });
        if (onForeground) onMessage(_messaging, onForeground);
        return { ok: true, token };
    } catch (e) {
        return { ok: false, reason: "error", error: String(e?.message || e) };
    }
}

export function pushPermission() {
    return typeof Notification !== "undefined" ? Notification.permission : "unsupported";
}
